import {
  BlockerContext,
  ButtonGroup,
  Form,
  FormButton,
  FormSubmitHandler,
  Tabs,
  WithoutBlocker,
} from 'platform/components';
import {Box} from 'platform/foundation';
import {DeepPartial} from 'utility-types';

import {useRef, useState} from 'react';
import {FieldErrors} from 'react-hook-form';

import {defaultTo, isNil} from 'ramda';

import {ApiError, TenantResponseBody} from '@dms/api/shared';
import {
  GetArticleResponse,
  GetWarehouseResponse,
  GetWarehousesResponse,
  PatchArticleRequest,
  usePatchArticleMutation,
} from '@dms/api/warehouse';
import i18n from '@dms/i18n';
import {warehouseRoutes} from '@dms/routes';
import {logError, queryParams, Section} from '@dms/shared';
import {handleWarehouseAclApiError} from '@dms/shared-aftersales';
import {CatalogFilter} from '@dms/warehouse/shared';
import {useWarehouseParams} from '@dms/warehouse/shared-components';

import {Nullish, suffixTestId, TestIdProps, useNavigate, useQueryState} from 'shared/web';

import type {EagGridMaximization} from 'features/eag-grid';

import {useArticleAllowedPermissions} from '../../../../hooks/useArticleAllowedPermissions';
import {CatalogsInformationTab} from './components/CatalogsInformationTab';
import {GeneralInformationTab} from './components/GeneralInformationTab';
import {PricesTab} from './components/PricesTab';
import {StockManagementTab} from './components/StockManagementTab';
import {useArticleFormRecalculation} from './hooks/useArticleFormRecalculation';
import {ArticleDetailForm, articleDetailFormSchema} from './types/ArticleDetailFormSchema';

interface OverviewProps extends TestIdProps {
  maximization?: EagGridMaximization;
  article: GetArticleResponse;
  warehouse: GetWarehouseResponse;
  warehouses: GetWarehousesResponse;
  tenant: TenantResponseBody;
  handlingUnitLabel: string;
}

type SubmitOptions = 'save' | 'saveAndBack';

export function Overview(props: OverviewProps) {
  const {articleId, warehouseId} = useWarehouseParams();
  const [activeTabId, setActiveTabId] = useQueryState(queryParams.COMPONENT_SECTIONS_TAB);
  const navigate = useNavigate();

  const withoutBlockerRef = useRef<WithoutBlocker>((callback) => callback());

  const allowedPermissions = useArticleAllowedPermissions(props.article);

  const defaultValues: DeepPartial<ArticleDetailForm> = {
    generalInformation: {
      basicInformation: {
        name: props.article?.name,
        description: props.article?.description,
        manufacturerNumber: props.article?.manufacturerNumber,
        manufacturerId: props.article?.manufacturerId,
        supplierId: props.article?.supplierId,
        supplierDiscountGroupId: props.article?.supplierDiscountGroupId,
        warehouseId: props.article?.warehouseId,
        storageLocationId: props.article?.storageLocationId,
        marketingCode: props.article?.marketingCode,
        workItemGroupId: props.article?.workItemGroupId,
        vatType: props.article?.vatType,
      },
      units: {
        handlingUnit: props.article?.handlingUnit,
        dispensingUnit: props.article?.dispensingUnit ?? undefined,
      },
    },
    prices: {
      pricesSettings: props.article?.pricesSettings,
      dispensingPrices: {
        ...props.article?.dispensingPrices,
        saleBaseMarginPercent: defaultTo(
          props.warehouse.salePriceMarginPercent,
          props.article?.dispensingPrices?.saleBaseMarginPercent
        ),
        saleBaseMarkUpPercent: defaultTo(
          props.warehouse.salePriceMarkupPercent,
          props.article?.dispensingPrices?.saleBaseMarkUpPercent
        ),
        saleBaseProfit: defaultTo(
          props.warehouse.salePriceProfitWithoutVat,
          props.article?.dispensingPrices?.saleBaseProfit
        ),
      },
    },
    stockManagement: {
      stockManagementType: props.article?.stockManagement?.stockManagementType,
      stockManagementMinQuantity: props.article?.stockManagement?.stockManagementMinQuantity,
      stockManagementMaxQuantity: props.article?.stockManagement?.stockManagementMaxQuantity,
      isOneTimeSale: defaultTo(false, props.article?.isOneTimeSale),
    },
  };

  const [leafId, setLeafId] = useState<string | Nullish>(props.article?.treeFolder.leafId);
  const submitOption = useRef<SubmitOptions>('save');

  const [patchArticle] = usePatchArticleMutation();

  const {handleChange, isArticleCalculateLoading} = useArticleFormRecalculation({
    articleId,
    warehouseId,
    defaultValues,
  });

  const manufacturerId = props.article?.manufacturerId ?? undefined;

  const catalogFilter: CatalogFilter = {
    branchId: props.warehouse.branchId,
    supplierId: props.article?.supplierId,
    manufacturerId: manufacturerId!,
    // eslint-disable-next-line typescript/no-non-null-asserted-optional-chain -- Muted pre-existing violation
    manufacturerNumber: props.article?.manufacturerNumber!,
  };

  const handleNavigateBack = () => {
    withoutBlockerRef.current(() => navigate(warehouseRoutes.articleList));
  };

  /**
   * The article is already saved at this point, but the form still reports its fields as dirty
   * (it is never reset - the refetched article remounts the whole detail page instead).
   * Navigating through the blocker would therefore open the "unsaved changes" dialog and swallow
   * the navigation, so the user would stay on the detail page after a successful save.
   */
  const handleNavigateBackAfterSave = () => {
    withoutBlockerRef.current(() => navigate(warehouseRoutes.articleList));
  };

  const submitForm = async (data: ArticleDetailForm, blockerContext?: BlockerContext) => {
    if (isNil(data.generalInformation.basicInformation.supplierId)) {
      return logError('supplierId is not defined');
    }

    if (isNil(data.generalInformation.basicInformation.manufacturerId)) {
      return logError('manufacturerId is not defined');
    }

    if (isNil(leafId)) {
      return logError('leafId is not defined');
    }

    const modifiedArticle: PatchArticleRequest['body'] = {
      // general information
      supplierId: data.generalInformation.basicInformation.supplierId,
      manufacturerId: data.generalInformation.basicInformation.manufacturerId,
      name: data.generalInformation.basicInformation.name,
      description: data.generalInformation.basicInformation.description,
      manufacturerNumber: data.generalInformation.basicInformation.manufacturerNumber,
      vatType: data.generalInformation.basicInformation.vatType,
      storageLocationId: data.generalInformation.basicInformation.storageLocationId,
      treeFolder: {leafId},
      marketingCode: data.generalInformation.basicInformation.marketingCode,
      workItemGroupId: data.generalInformation.basicInformation.workItemGroupId,
      handlingUnit: data.generalInformation.units.handlingUnit,
      dispensingUnit: data.generalInformation.units.dispensingUnit,
      // prices
      pricesSettings: data.prices.pricesSettings,
      dispensingPrices: data.prices.dispensingPrices,
      // stock management
      stockManagement: {
        stockManagementType: data.stockManagement.stockManagementType,
        stockManagementMinQuantity:
          data.stockManagement.stockManagementType === 'MANUAL'
            ? undefined
            : data.stockManagement.stockManagementMinQuantity,
        stockManagementMaxQuantity:
          data.stockManagement.stockManagementType === 'MANUAL'
            ? undefined
            : data.stockManagement.stockManagementMaxQuantity,
      },
      isOneTimeSale: data.stockManagement.isOneTimeSale,
    };

    await patchArticle({articleId, warehouseId, body: modifiedArticle, isAclScoped: true})
      .unwrap()
      .then(() => {
        if (submitOption.current === 'save') {
          return;
        }

        if (blockerContext?.wasSubmittedFromBlocker) {
          return;
        }

        handleNavigateBackAfterSave();
      })
      .catch((error: ApiError) =>
        handleWarehouseAclApiError(error, {signalApiError: blockerContext?.reportApiFailure})
      );
  };

  const handleSubmit: FormSubmitHandler<ArticleDetailForm> = async (
    data,
    _setErrors,
    _reset,
    blockerContext
  ) => {
    await submitForm(data, blockerContext);
  };

  const handleNavigateToInvalidTab = (errors: FieldErrors<ArticleDetailForm>) => {
    const isGeneralInformationTabValid = isNil(errors.generalInformation);
    const isPriceTabValid = isNil(errors.prices);
    const isStockManagementTabValid = isNil(errors.stockManagement);

    if (!isGeneralInformationTabValid) {
      return withoutBlockerRef.current(() => setActiveTabId(TABS_IDS.generalInformation));
    }

    if (!isPriceTabValid) {
      return withoutBlockerRef.current(() => setActiveTabId(TABS_IDS.prices));
    }

    if (!isStockManagementTabValid) {
      return withoutBlockerRef.current(() => setActiveTabId(TABS_IDS.stockManagement));
    }
  };

  return (
    <Section isFullHeight>
      <Form<ArticleDetailForm>
        isFullHeight
        isDisabled={!allowedPermissions.canEdit}
        shouldWatchForUnsavedChanges
        experimentalZodSchema={articleDetailFormSchema}
        defaultValues={defaultValues}
        onSubmit={handleSubmit}
        onInvalidSubmit={(errors) => handleNavigateToInvalidTab(errors)}
      >
        {(control, formApi, _navigateWithoutBlocker, withoutBlocker) => {
          withoutBlockerRef.current = withoutBlocker;

          formApi.watch((data, test) => {
            if (JSON.stringify(data) === JSON.stringify(defaultValues)) {
              return;
            }

            handleChange(data, test, formApi);
          });

          return (
            <Box height="100%">
              <Box height="95%">
                <Tabs
                  isFullHeight
                  activeTabId={activeTabId}
                  onChange={(tabId) => withoutBlocker(() => setActiveTabId(tabId))}
                  variant="condensed"
                  tabs={[
                    {
                      title: i18n.t('entity.warehouse.labels.generalInformation'),
                      id: TABS_IDS.generalInformation,
                      content: (
                        <GeneralInformationTab
                          articleId={articleId}
                          article={props.article}
                          control={control}
                          formApi={formApi}
                          manufacturerId={manufacturerId}
                          warehouse={props.warehouse}
                          warehouses={props.warehouses}
                          leafId={leafId}
                          leafIdChange={setLeafId}
                          isNameDisabled={!allowedPermissions.canRename}
                          isCategoryDisabled={!allowedPermissions.canEdit}
                          // Supplier, marketing code and VAT type recalculate the sale prices
                          isPricingDisabled={!allowedPermissions.canChangePrices}
                        />
                      ),
                    },
                    {
                      title: i18n.t('entity.warehouse.labels.prices'),
                      id: TABS_IDS.prices,
                      content: (
                        <PricesTab
                          article={props.article}
                          tenant={props.tenant}
                          control={control}
                          formApi={formApi}
                          isPricingDisabled={!allowedPermissions.canChangePrices}
                        />
                      ),
                    },
                    {
                      title: i18n.t('entity.warehouse.labels.stockManagement'),
                      id: TABS_IDS.stockManagement,
                      content: (
                        <StockManagementTab
                          article={props.article}
                          control={control}
                          formApi={formApi}
                          handlingUnitLabel={props.handlingUnitLabel}
                        />
                      ),
                    },
                    {
                      title: i18n.t('entity.warehouse.labels.catalogsInformation'),
                      id: TABS_IDS.catalogsInformation,
                      content: (
                        <CatalogsInformationTab
                          maximization={props.maximization}
                          catalogFilter={catalogFilter}
                        />
                      ),
                    },
                  ]}
                />
                <Box paddingTop={4}>
                  <ButtonGroup align="right">
                    <FormButton
                      variant="secondary"
                      control={control}
                      title={i18n.t('general.actions.discardChanges')}
                      onClick={handleNavigateBack}
                      data-testid={suffixTestId('actions.discard', props)}
                    />
                    <FormButton
                      isLoading={isArticleCalculateLoading}
                      variant="secondary"
                      onClick={async () => {
                        submitOption.current = 'saveAndBack';
                        const isValid = await formApi.trigger();
                        if (isValid) {
                          const values = formApi.getValues();
                          await submitForm(values);
                        }
                      }}
                      control={control}
                      title={i18n.t('entity.warehouse.actions.saveAndBackToArticleList')}
                      data-testid={suffixTestId('actions.saveAndBackToArticle', props)}
                    />
                    <FormButton
                      isLoading={isArticleCalculateLoading}
                      onClick={() => {
                        submitOption.current = 'save';
                      }}
                      control={control}
                      type="submit"
                      title={i18n.t('general.actions.saveChanges')}
                      data-testid={suffixTestId('actions.submit', props)}
                    />
                  </ButtonGroup>
                </Box>
              </Box>
            </Box>
          );
        }}
      </Form>
    </Section>
  );
}

const TABS_IDS = {
  generalInformation: 'generalInformation',
  prices: 'prices',
  stockManagement: 'stockManagement',
  catalogsInformation: 'catalogsInformation',
};
